import React from 'react';
import { StyleSheet, css, CSSProperties } from 'aphrodite/no-important';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { getGeneratedGlobalStyles } from '../../../../../typography/globalTypographyStyles';
import { useTheme } from '../../../../../theming/useTheme';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { Link } from '../../../../partials/link/Link';

const componentConfigThemeKey = 'ctaButton';

export interface CtaDataProps {
  href: string,
  text: string,
}

interface CtaButtonComponentThemeProps {
  backgroundColorNormal: string,
  textColorNormal: string,
  borderColorNormal: string,
  backgroundColorHover: string,
  textColorHover: string,
  borderColorHover: string,
  backgroundColorActive: string,
  textColorActive: string,
  borderColorActive: string,
  surfaceStyle: CSSProperties
  vrLineHeight: VrLineHeight,
}

const getStyle = ({
  backgroundColorNormal,
  textColorNormal,
  borderColorNormal,
  backgroundColorHover,
  textColorHover,
  borderColorHover,
  backgroundColorActive,
  textColorActive,
  borderColorActive,
  vrLineHeight }: CtaButtonComponentThemeProps) => {
  return StyleSheet.create({
    ctaButton: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: backgroundColorNormal,
      color: textColorNormal,
      border: `${borderColorNormal} 1px solid`,
      cursor: 'pointer',
      borderWidth: '1px',
      textDecoration: 'none',
      height: '32px',
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: textColorHover,
        border: `${borderColorHover} 1px solid`,
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: textColorActive,
        border: `${borderColorActive} 1px solid`,
      },
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 2}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        padding: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
      },
    },

  });
};

export const CtaButtonComponent: React.FunctionComponent<CtaButtonComponentThemeProps & CtaDataProps> = props => {
  const { surfaceStyle, href, text } = props;
  const style = getStyle(props);
  const linkTextStyle = StyleSheet.create(getGeneratedGlobalStyles().p());
  return (
    <Link className={css(style.ctaButton, StyleSheet.create({ surfaceStyle }).surfaceStyle, linkTextStyle.style)} href={href}>
      {text}
    </Link>
  );
};

const mapThemeProps = (theme: CtaButtonComponentThemeProps) => {
  return {
    backgroundColorNormal: theme.backgroundColorNormal,
    textColorNormal: theme.textColorNormal,
    borderColorNormal: theme.borderColorNormal,
    backgroundColorHover: theme.backgroundColorHover,
    textColorHover: theme.textColorHover,
    borderColorHover: theme.borderColorHover,
    backgroundColorActive: theme.backgroundColorActive,
    textColorActive: theme.textColorActive,
    borderColorActive: theme.borderColorActive,
    surfaceStyle: theme.surfaceStyle,
    vrLineHeight: theme.vrLineHeight,
  };
};

export const CtaButton = (props: CtaDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><CtaButtonComponent {...themeContext} {...props} /></ErrorBoundary>;
};
